import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CancelSmallMinor, ProductReturnsMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { LinkSubcontractorTableContext } from './LinkSubcontractorTable.context'
import { CircularProgress } from '@mui/material'
import { SensitiveActionWithPictureModal } from 'src/components/shared/SubmitActionWithPictureModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { showToast } from 'src/utils/toast'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { useDispatch } from 'react-redux'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  AssignContractorsToCustomer,
  UnassignContractorsFromCustomer,
} from 'src/constants/actionTypes'
import { LinkSubcontractorContext } from '../LinkSubcontractor.context'
import { SubcontractorRequestStatus } from 'src/constants/subcontractorRequests/enum'

const LinkSubcontractorActionMenuComponent = () => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const { reload } = useContext(ReloadWrapperContext)
  const { selectedMCCustomerId } = useContext(LinkSubcontractorContext)
  const { contractorToAction, actionAnchor, closeLinkSubcontractorItemToAction } = useContext(
    LinkSubcontractorTableContext,
  )

  const isOpen = Boolean(actionAnchor)

  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false)

  const handleOpenConfirmPopup = useCallback(() => {
    setIsConfirmPopupOpen(true)
  }, [])

  const handleCloseConfirmPopup = useCallback(() => {
    setIsConfirmPopupOpen(false)
  }, [])

  const handleRemoveSubmit = useCallback(async () => {
    if (!contractorToAction) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        UnassignContractorsFromCustomer.request({
          selectedContractorIds: [contractorToAction.id],
          customerId: selectedMCCustomerId,
        }),
      )

      showToast('Contractor removed.')

      reload()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
      handleCloseConfirmPopup()
    }
  }, [contractorToAction, reload])

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!contractorToAction) {
      return []
    }

    const res: MenuActionItem[] = []

    if (
      contractorToAction.status === SubcontractorRequestStatus.Inactive ||
      contractorToAction.status === SubcontractorRequestStatus.Rejected
    ) {
      res.push({
        label: 'Submit again',
        icon: isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <ProductReturnsMinor width={20} height={20} />
        ),
        onClick: async () => {
          setIsLoading(true)

          try {
            await createAsyncAction(
              dispatch,
              AssignContractorsToCustomer.request({
                partnerDriverIds: [contractorToAction.id],
                customerId: selectedMCCustomerId,
              }),
            )

            reload()
          } catch (err) {
            handleApiError(err)
          } finally {
            setIsLoading(false)
            closeLinkSubcontractorItemToAction()
          }
        },
      })
    }

    if (
      contractorToAction.status === SubcontractorRequestStatus.Active ||
      contractorToAction.status === SubcontractorRequestStatus.PendingCustomerReview
    ) {
      res.push({
        label: 'Remove',
        icon: isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <CancelSmallMinor width={20} height={20} />
        ),
        onClick: async () => {
          handleOpenConfirmPopup()
          closeLinkSubcontractorItemToAction()
        },
      })
    }

    return res
  }, [contractorToAction, isLoading, selectedMCCustomerId, reload])

  return (
    <>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeLinkSubcontractorItemToAction}
      />
      <SensitiveActionWithPictureModal
        isOpen={isConfirmPopupOpen}
        title="Are you sure you want to remove contractor?"
        text="By removing the contractor, they will disappear from the customer's system. You can link the contractor again at any time."
        submitLabel="Remove"
        onClose={handleCloseConfirmPopup}
        onSubmit={handleRemoveSubmit}
        imageSrc={NotFoundImage}
      />
    </>
  )
}

export const LinkSubcontractorActionMenu = memo(LinkSubcontractorActionMenuComponent)
